<template>
    <widget-wrapper
    extraClass="exchanges"
    :variant="variant"
    :title="t('home.widgets.exchanges.title')"
    :applyErrorState="applyErrorState"
    @redirectToFeature="navigateByName(routeList.EXCHANGES_NAME)">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- Exchange Form -->
        <div v-if="!showLoader" class="form-wrapper">
            <!-- Amount -->
            <input-amount
            class="amount-input"
            :params="{ ...amountInputParams }"
            :inputValue="formData.amount"
            @input="[formData.amount = $event, amountInputParams.errorState = false]"
            @onBlur="amountValidation"/>
            <!-- Currency Drops & Button -->
            <div class="aligner-helper" :class="{ 'mobile': getMobileAppState }">
                <!-- Currency Dropdown -->
                <dropdown-currency
                class="currency-dropdown"
                :key="formData.currencyFrom"
                :params="currencyDropdownFromParams"
                :options="currencyListForDropdown"
                :selectedOption="formData.currencyFrom"
                @clicked="[$event !== 'AOA' ? formData.currencyTo = 'AOA' : (formData.currencyFrom === 'AOA' ? null : formData.currencyTo = 'USD'), formData.currencyFrom = $event]"/>
                <!-- Currency Dropdown -->
                <dropdown-currency
                class="currency-dropdown"
                :key="formData.currencyTo + showConvertingLoader"
                :params="currencyDropdownToParams"
                :options="formData.currencyFrom === 'AOA' ? currencyListForDropdown.filter(el => el.id !== 'AOA') : currencyListForDropdown.filter(el => el.id === 'AOA')"
                :selectedOption="formData.currencyTo"
                @clicked="formData.currencyTo = $event"/>
                <!-- Submit Button -->
                <button-primary
                icon="currency-exchange"
                iconPosition="right"
                buttonSize="small"
                :label="t('home.widgets.exchanges.convert')"
                @clicked="convertButtontHandler"/>
            </div>
            <!-- Result -->
            <div class="conversor-result" :class="{ 'mobile': getMobileAppState }">
                <!-- Converting loader -->
                <loaders-generic v-if="showConvertingLoader" fullSize/>
                <div v-if="convertedAmount && !showConvertingLoader && !showConvertingError" class="amount-wrapper">
                    <div class="currency">{{convertedCurrency}}</div>
                    <div class="amount">{{amountFormat(convertedAmount, null, null, true, 5)}}</div>
                </div>
                <div v-if="convertedAmount && !showConvertingLoader && !showConvertingError" class="update-message">
                    {{t('exchanges.table.lastUpdateDate', { date: parsedLastUpdateDate })}}
                </div>
                <infobox-generic v-if="!showConvertingLoader && showConvertingError" state="negative" :label="t('home.widgets.exchanges.errorConversor')"/>
            </div>
        </div>
    </widget-wrapper>
</template>

<script setup>
import WidgetWrapper from '@/components/features/home/WidgetWrapper'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import InputAmount from '@/components/generic/inputs/Amount'
import DropdownCurrency from '@/components/generic/dropdowns/currency/Currency'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import InfoboxGeneric from '@/components/generic/infobox/Generic'

/* Vue & Assets */
import { ref, computed, watch, onMounted } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestExchangeTableWithValue } = mapActions(moduleMapper.HOME)
const { getExchangeTableList, getExchangeTableWithValue, getLastUpdateDate } = mapGetters(moduleMapper.HOME)
const { getMobileAppState } = mapGetters()

/* Props
@showExchangesLoader {Boolean} - prop to tell widget fetch state
@applyExchangesErrorState {Boolean} - prop to tell widget fetch error state
*/
const props = defineProps({
    showExchangesLoader: Boolean,
    applyExchangesErrorState: Boolean
})

/* Data */
const showLoader = ref(props.showExchangesLoader)
const applyErrorState = ref(props.applyExchangesErrorState)
const showConvertingLoader = ref(false)
const showConvertingError = ref(false)

/*************************************************
*                                                *
*                  Request Logics                *
*                                                *
*************************************************/
watch(() => props.showExchangesLoader, (newVal, oldVal) => {
    if (!props.showExchangesLoader) {
        convertButtontHandler('firstRequest')
        showLoader.value = false
    }
})

watch(() => props.applyExchangesErrorState, (newVal, oldVal) => {
    if (props.applyExchangesErrorState) applyErrorState.value = true
})

/*************************************************
*                                                *
*                      Form Data                 *
*                                                *
*************************************************/
const formData = ref({
    amount: '',
    currencyFrom: 'AOA',
    currencyTo: 'USD'
})
const convertedCurrency = ref('USD')

const amountInputParams = ref({
    labelText: t('home.widgets.exchanges.amount'),
    errorState: false,
    errorMessage: null,
    placeholder: '1,00'
})

const amountValidation = () => {
    if (Number(formData.value.amount) === 0) {
        amountInputParams.value.errorMessage = t('invalidAmount')
        amountInputParams.value.errorState = true
    }
}

const currencyDropdownFromParams = ref({
    labelText: t('home.widgets.exchanges.currencyFrom')
})

const currencyDropdownToParams = ref({
    labelText: t('home.widgets.exchanges.currencyTo')
})

/* Returns an array with a exchange rates grouped by currency */
const groupByCurrency = computed(() => {
    const groupObject = getExchangeTableList.value.reduce((acc, item) => {
        acc[item.currency] = acc[item.currency] ?? []
        acc[item.currency].push(item)

        return acc
    }, {})

    return Object.getOwnPropertyNames(groupObject).map(attributeName => groupObject[attributeName])
})

const currencyListForDropdown = computed(() => {
    const groupedCurrencies = groupByCurrency.value.map(obj => ({
        id: obj[1].currency,
        currency: obj[1].currency,
        description: obj[1].description,
        icon: `flags/${flagCurrenciesIconMapper(obj[1].currency)}`
    }))

    return [
        {
            id: 'AOA',
            currency: 'AOA',
            description: 'Kwanza',
            icon: `flags/${flagCurrenciesIconMapper('AKZ')}`
        },
        ...groupedCurrencies
    ]
})

const convertedAmount = ref(null)

const convertButtontHandler = async (firstRequest) => {
    showConvertingError.value = false
    amountInputParams.value.errorState = false

    if (!Number(formData.value.amount) && !firstRequest) {
        amountInputParams.value.errorState = true
        amountInputParams.value.errorMessage = !formData.value.amount ? t('mandatoryFieldError') : t('invalidAmount')
        return
    }

    showConvertingLoader.value = true

    const payload = {
        currency: formData.value.currencyFrom === 'AOA' ? 'AKZ' : formData.value.currencyFrom,
        originValue: firstRequest ? 1 : formData.value.amount,
        quotationDate: new Date()
    }

    try {
        await requestExchangeTableWithValue(payload)
        handleCurrencyConvertion()
        showConvertingLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showConvertingLoader.value = false
        showConvertingError.value = true
	}
}

const handleCurrencyConvertion = () => {
    if (formData.value.currencyFrom === 'AOA') {
        convertedAmount.value = getExchangeTableWithValue.value.filter(obj => obj.currency === formData.value.currencyTo)[0].convertedSellValue
    } else {
        convertedAmount.value = getExchangeTableWithValue.value[0].convertedSellValue
    }

    convertedCurrency.value = formData.value.currencyTo
}

/* Parsed last update date */
const parsedLastUpdateDate = computed(() => {
    /* Added replace with regex to capitalize the month */
    return getDateWithTime(getLastUpdateDate.value).replace(/\b[a-z]/, match => match.toUpperCase())
})

onMounted(() => {
    if (!props.showExchangesLoader) {
        convertButtontHandler('firstRequest')
        showLoader.value = false
    }
})
// fecthExchangeTableList()
</script>

<style lang="scss" scoped>
.form-wrapper {
    width: 100%;
    padding: 16px 24px 0 24px;

    display: flex;
    flex-direction: column;
    gap: 16px;

    .input-elements-container {
        height: 64px;
        overflow: visible;

        :deep(.bottom-elements-container) {
            padding-top: 2px;
        }
    }

    .aligner-helper {
        width: 100%;

        display: flex;
        gap: 16px;

        .dropdown-wrapper {
            max-width: 148px;

            :deep(.dropdown-animation-wrapper) {
                max-height: 200px;
            }
        }

        .button-primary {
            margin-top: 24px;
            flex-grow: 1;
        }

        &.mobile {
            flex-wrap: wrap;
            gap: 8px;

            .dropdown-wrapper {
                max-width: unset;
                width: calc(50% - 4px);
            }

            .button-primary {
                width: 100%;
                margin-top: 8px;
            }
        }
    }

    .conversor-result {
        width: 100%;
        height: 102px;
        padding: 0 16px;

        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 8px;

        background-color: var(--secondary-light-3);
        border-radius: 8px;

        .amount-wrapper {
            width: 100%;
            padding: 0 16px;

            display: flex;
            gap: 4px;

            .currency {
                width: fit-content;

                @include H2-Regular;
                color: var(--secondary);
            }

            .amount {
                width: 100%;

                @include H2-Medium;
                color: var(--primary);
            }
        }

        .update-message {
            width: 100%;
            padding: 0 16px;
            flex-shrink: 0;

            @include H6-Regular;
            color: var(--secondary-dark-1);
        }

        &.mobile {
            height: 64px;

            .amount-wrapper {
                .currency {
                    @include H3-Regular;
                }

                .amount {
                    @include H3-Medium;
                }
            }

            .update-message {
                @include H9-Regular;
            }
        }
    }
}
</style>
